<script setup lang="ts">
import { mdiLock } from "@mdi/js";
import EventPriceIndicator from "./EventPriceIndicator.vue";
import { IOutcome } from "~/types/sports/outcome";
import { useBetbook } from "~/store/betbook";
import formatOdds from "~/utils/formatOdds";
import { betwayBoostedOdds } from "~/assets/betwayIcons";
import { useBetslip } from "~/store/betslip";
import { IMarket } from "~/types/sports/market";
import { totalGoalMarkets } from "~/utils/constants";

interface Props {
	outcome: IOutcome;
	market: IMarket | undefined;
	outcomeClass?: string;
	eventId: number;
	hideIndicators?: boolean;
}

const props = defineProps<Props>();
const store = useBetbook();
const betslip = useBetslip();
const lastPrice = ref<number>();
const priceAction = ref<"inc" | "dec">();
const score = computed(() => store.scores.get(props.eventId));
const isSelectionActive = ref(false);
const price = computed(() => {
	try {
		return store.prices.get(props.outcome.outcomeId);
	} catch (e) {
		return null;
	}
});

const isDisabled = computed(() => {
	return !price.value || props.outcome.isTradingActive === false || props.outcome.shouldDisplay === false || price?.value.priceDecimal === 1;
});

onMounted(() => {
	if (price.value) lastPrice.value = price.value.priceDecimal;
});

watchEffect(() => {
	if (lastPrice.value && price.value && lastPrice.value !== price.value.priceDecimal) {
		if (price.value.priceDecimal < lastPrice.value) {
			priceAction.value = "dec";
		} else if (price.value.priceDecimal > lastPrice.value) {
			priceAction.value = "inc";
		}
		lastPrice.value = price.value.priceDecimal;
		setTimeout(() => {
			priceAction.value = undefined;
		}, 2000);
	}
});

const activeSelections = computed(() => betslip.selection.map((x) => x.outcomeId));
watchEffect(() => {
	if (activeSelections.value.includes(props.outcome.outcomeId)) {
		isSelectionActive.value = true;
	} else if (isSelectionActive.value === true) isSelectionActive.value = false;
});

const handleSelection = () => {
	betslip.handleSelection({
		event: store.events.get(props.eventId),
		market: props.market,
		outcome: props.outcome,
		price: price.value,
		score: score.value,
	});
};
</script>

<template>
	<div
		v-if="!isDisabled && price"
		@click="
			(e) => {
				e.preventDefault();
				handleSelection();
			}
		"
		:class="[
			outcomeClass || 'md:h-8 h-[52px]',
			'flex flex-1 relative items-center justify-center rounded-md border  border-identity-100 dark:border-dark-400 font-bold cursor-pointer overflow-hidden text-ellipsis  select-none',
			isSelectionActive ? 'bg-identity !border-identity text-light-50' : 'bg-identity-50 dark:bg-dark-800',
		]"
	>
		<EventPriceIndicator
			v-if="priceAction === 'inc' && hideIndicators !== true"
			class="absolute -translate-x-1/2 top-2 md:top-[2px] left-1/2"
			:indicator-class="[isSelectionActive ? 'bg-light-50' : 'bg-primary-500']"
		/>
		<UiIcon class="w-4 h-4 fill-warning-600" :path="betwayBoostedOdds" v-if="outcome.isBoosted" />
		<span :class="[priceAction === 'dec' && 'text-error-500', priceAction === 'inc' && 'text-primary-500', isSelectionActive && '!text-light-50']">
			{{ formatOdds(price.priceDecimal, price.numerator, price.denominator) }}
		</span>
		<EventPriceIndicator
			v-if="priceAction === 'dec' && hideIndicators !== true"
			class="absolute -translate-x-1/2 bottom-2 md:bottom-[2px] left-1/2 rotate-180"
			:indicator-class="[isSelectionActive ? 'bg-light-50' : 'bg-error-500']"
		/>
	</div>
	<!-- Display locked price -->
	<div
		v-else
		class="flex items-center justify-center flex-1 border rounded-md select-none border-light-500 dark:border-dark-400 bg-light-500 dark:bg-dark-400"
		:class="outcomeClass || 'md:h-8 h-[52px]'"
	>
		<UiIcon class="w-4 h-4 dark:fill-light-500" :path="mdiLock" />
	</div>
</template>
